import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { FormationWizard } from "@/components/FormationWizard";
import { api } from "@/lib/api";
import { normalizeEntityType } from "@/lib/entityTypes";
import {
  Buildings, CheckCircle, Circle, ArrowRight, ShieldCheck, IdentificationCard,
  MapPin, Trash, Plus, Bank, ArrowSquareOut, CaretDown,
} from "@phosphor-icons/react";

export default function Formation() {
  const [partner, setPartner] = useState(null);
  const [stages, setStages] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = () => {
    Promise.all([
      api.get("/formation/partner"),
      api.get("/formation/applications"),
    ]).then(([p, a]) => {
      setPartner(p.data.partner);
      setStages(p.data.stages || []);
      setApps(a.data || []);
    }).catch(() => toast.error("Couldn't load the Formation module."))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openForm = () => { setShowForm(true); };
  const openPartnerLink = async (placement) => {
    if (!partner?.url) return;
    try { await api.post("/formation/partner/click", { placement }); } catch {}
    window.open(partner.url, "_blank", "noopener");
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-5xl mx-auto px-6 lg:px-8 py-12">
        <span className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: "var(--bk-accent)" }}>Start a business</span>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2 mb-2" style={{ color: "var(--bk-text)" }}>
          Form your business & get your EIN
        </h1>
        <p className="text-base max-w-2xl mb-8" style={{ color: "var(--bk-text-2)" }}>
          File your entity the right way and get your federal Tax ID (EIN) — all through our formation partner. IronRoot keeps your paperwork, deadlines, and books in one place from day one.
        </p>

        {/* Partner hero */}
        {partner && (
          <section className="rounded-2xl p-7 text-white mb-8 relative overflow-hidden" style={{ background: "var(--bk-gradient-primary)" }} data-testid="formation-partner">
            <div className="absolute -top-16 -right-10 w-56 h-56 rounded-full" style={{ background: "rgba(255,255,255,0.10)" }} />
            <div className="relative flex flex-wrap items-start justify-between gap-4">
              <div className="max-w-xl">
                <div className="flex items-center gap-2">
                  <Buildings size={22} weight="fill" />
                  <span className="text-xs font-semibold uppercase tracking-[0.14em]" style={{ color: "rgba(255,255,255,0.85)" }}>Powered by {partner.name}</span>
                </div>
                <p className="font-display text-2xl font-bold mt-3">{partner.offer}</p>
                <p className="text-sm mt-1" style={{ color: "rgba(255,255,255,0.9)" }}>{partner.tagline}</p>
                <ul className="mt-4 space-y-1.5">
                  {partner.features.map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm" style={{ color: "rgba(255,255,255,0.95)" }} data-testid={`partner-feature-${i}`}>
                      <CheckCircle size={16} weight="fill" /> {f}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <button onClick={openForm} data-testid="start-formation-btn" className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-bold" style={{ background: "#fff", color: "var(--bk-accent)" }}>
                  <Plus size={18} weight="bold" /> Start my formation
                </button>
                <button onClick={() => openPartnerLink("formation-hero")} data-testid="partner-link-hero" className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-semibold border" style={{ borderColor: "rgba(255,255,255,0.5)", color: "#fff" }}>
                  Visit {partner.name} <ArrowSquareOut size={15} weight="bold" />
                </button>
              </div>
            </div>
          </section>
        )}

        {/* Interactive Step 1 wizard */}
        {showForm && (
          <FormationWizard partner={partner} onSaved={() => { setShowForm(false); load(); }} onCancel={() => setShowForm(false)} />
        )}

        {/* Applications */}
        {loading ? (
          <p className="text-sm" style={{ color: "var(--bk-text-2)" }}>Loading…</p>
        ) : apps.length === 0 && !showForm ? (
          <div className="bk-card p-10 text-center" data-testid="formation-empty">
            <Buildings size={40} weight="duotone" color="var(--bk-accent)" className="mx-auto mb-3" />
            <p className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}>No businesses in formation yet</p>
            <p className="text-sm mt-1 mb-4" style={{ color: "var(--bk-text-2)" }}>Start your business formation in a few minutes — we'll track every step from filing to EIN.</p>
            <button onClick={openForm} data-testid="empty-start-btn" className="bk-btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold">
              <Plus size={18} weight="bold" /> Start my formation
            </button>
          </div>
        ) : (
          <div className="space-y-5" data-testid="formation-list">
            {apps.map((app) => (
              <FormationCard key={app.id} app={app} stages={stages} partner={partner} onChange={load} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function FormationCard({ app, stages, partner, onChange }) {
  const [busy, setBusy] = useState(false);
  const [ein, setEin] = useState(app.ein_number || "");
  const stageIdx = useMemo(() => stages.findIndex((s) => s.key === app.status), [stages, app.status]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/formation/partner/click", { placement: "formation-card-submit" }).catch(() => {});
      const { data } = await api.post(`/formation/applications/${app.id}/submit`);
      window.open(data.partner_url, "_blank", "noopener");
      toast.success(`Opening ${partner?.name} — finish filing there, then update the status here.`);
      onChange();
    } catch { toast.error("Couldn't continue to the partner."); }
    finally { setBusy(false); }
  };

  const setStatus = async (status) => {
    setBusy(true);
    try {
      await api.patch(`/formation/applications/${app.id}`, { status });
      toast.success("Status updated.");
      onChange();
    } catch { toast.error("Couldn't update status."); }
    finally { setBusy(false); }
  };

  const saveEin = async () => {
    if (!ein.trim()) return;
    setBusy(true);
    try {
      await api.patch(`/formation/applications/${app.id}`, { ein_number: ein.trim(), status: "ein_issued" });
      toast.success("EIN saved — you're official!");
      onChange();
    } catch { toast.error("Couldn't save the EIN."); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Remove the formation record for "${app.business_name}"?`)) return;
    setBusy(true);
    try { await api.delete(`/formation/applications/${app.id}`); toast.success("Removed."); onChange(); }
    catch { toast.error("Couldn't remove it."); }
    finally { setBusy(false); }
  };

  const isComplete = app.status === "complete" || app.status === "ein_issued";

  return (
    <section className="bk-card p-6" data-testid={`formation-card-${app.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: "var(--bk-page)" }}>
            <Buildings size={22} weight="duotone" color="var(--bk-accent)" />
          </div>
          <div>
            <h3 className="font-display text-lg font-bold leading-tight" style={{ color: "var(--bk-text)" }} data-testid={`formation-name-${app.id}`}>{app.business_name}</h3>
            <div className="flex flex-wrap items-center gap-2 mt-1 text-xs" style={{ color: "var(--bk-text-2)" }}>
              <span className="inline-flex items-center gap-1"><MapPin size={13} weight="fill" /> {app.state}</span>
              <span className="px-2 py-0.5 rounded-full font-semibold" style={{ background: "var(--bk-page)", color: "var(--bk-accent)" }}>{normalizeEntityType(app.entity_type)}</span>
              <span className="px-2 py-0.5 rounded-full font-semibold" style={{ background: "var(--bk-page)", color: "var(--bk-text-2)" }} data-testid={`formation-tax-${app.id}`}>
                {app.tax_treatment === "s_corp_election" ? "S-corp election planned (Form 2553 — CPA review)" : "Default tax treatment · S-corp election is a later decision"}
              </span>
              {app.need_ein && <span className="inline-flex items-center gap-1"><IdentificationCard size={13} weight="fill" /> EIN</span>}
              {app.registered_agent === "partner" && <span className="inline-flex items-center gap-1"><ShieldCheck size={13} weight="fill" /> Registered agent</span>}
            </div>
          </div>
        </div>
        <button onClick={remove} disabled={busy} data-testid={`formation-delete-${app.id}`} className="p-2 rounded-md hover:bg-[#FDECE8]" title="Remove" style={{ color: "#B23B1F" }}><Trash size={16} weight="bold" /></button>
      </div>

      {/* Pipeline */}
      <div className="mt-6 flex items-center" data-testid={`formation-pipeline-${app.id}`}>
        {stages.map((s, i) => {
          const done = i <= stageIdx;
          const current = i === stageIdx;
          return (
            <div key={s.key} className="flex items-center" style={{ flex: i === stages.length - 1 ? "0 0 auto" : "1 1 0%" }}>
              <div className="flex flex-col items-center text-center" style={{ width: 0, minWidth: 0 }}>
                {done ? <CheckCircle size={24} weight="fill" color="var(--bk-success)" /> : <Circle size={24} weight={current ? "bold" : "regular"} color={current ? "var(--bk-accent)" : "var(--bk-border)"} />}
              </div>
              {i < stages.length - 1 && <div className="h-0.5 flex-1 mx-1" style={{ background: i < stageIdx ? "var(--bk-success)" : "var(--bk-border)" }} />}
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex justify-between text-[10px] font-semibold uppercase tracking-wide" style={{ color: "var(--bk-text-2)" }}>
        {stages.map((s, i) => (
          <span key={s.key} className="text-center" style={{ width: `${100 / stages.length}%`, color: i === stageIdx ? "var(--bk-accent)" : undefined }}>{s.label}</span>
        ))}
      </div>

      {/* Actions */}
      <div className="mt-6 pt-5 border-t flex flex-wrap items-center gap-3" style={{ borderColor: "var(--bk-border)" }}>
        {app.status === "draft" ? (
          <button onClick={submit} disabled={busy} data-testid={`formation-continue-${app.id}`} className="bk-btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-60">
            Continue with {partner?.name || "Bizee"} <ArrowRight size={16} weight="bold" />
          </button>
        ) : !isComplete ? (
          <>
            <button onClick={async () => { try { await api.post("/formation/partner/click", { placement: "formation-card-link" }); } catch {} window.open(partner?.url, "_blank", "noopener"); }} data-testid={`formation-partner-link-${app.id}`} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-semibold" style={{ border: "1px solid var(--bk-border)", color: "var(--bk-accent)" }}>
              Open {partner?.name || "Bizee"} <ArrowSquareOut size={14} weight="bold" />
            </button>
            <StatusPicker current={app.status} stages={stages} onPick={setStatus} disabled={busy} appId={app.id} />
          </>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: "var(--bk-success)" }} data-testid={`formation-done-${app.id}`}>
            <CheckCircle size={18} weight="fill" /> {app.wizard_answers?.registered_status === "yes" ? "Verified — documents on file" : "Business formed"}{app.ein_number ? ` · EIN ${app.ein_number}` : ""}
          </span>
        )}
      </div>

      {/* EIN capture */}
      {!isComplete && app.status !== "draft" && app.need_ein && (
        <div className="mt-4 flex flex-wrap items-end gap-2" data-testid={`formation-ein-${app.id}`}>
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs font-semibold flex items-center gap-1.5" style={{ color: "var(--bk-text-2)" }}><Bank size={14} weight="duotone" /> Got your EIN? Save it here</label>
            <input value={ein} onChange={(e) => setEin(e.target.value)} placeholder="12-3456789" data-testid={`formation-ein-input-${app.id}`} className="mt-1 w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} />
          </div>
          <button onClick={saveEin} disabled={busy || !ein.trim()} data-testid={`formation-ein-save-${app.id}`} className="px-4 py-2 rounded-full text-sm font-semibold text-white disabled:opacity-50" style={{ background: "var(--bk-accent)" }}>Save EIN</button>
        </div>
      )}

      {app.notes && <p className="mt-4 text-xs italic" style={{ color: "var(--bk-text-2)" }}>“{app.notes}”</p>}
    </section>
  );
}

function StatusPicker({ current, stages, onPick, disabled, appId }) {
  const nexts = stages.filter((s) => s.key !== "draft");
  return (
    <div className="relative inline-flex items-center gap-2 text-sm" style={{ color: "var(--bk-text-2)" }}>
      <span className="text-xs font-semibold uppercase tracking-wide">Update status</span>
      <div className="relative">
        <select value={current} onChange={(e) => onPick(e.target.value)} disabled={disabled} data-testid={`formation-status-select-${appId}`} className="appearance-none pl-3 pr-8 py-2 rounded-full border text-sm font-semibold bg-white" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
          {nexts.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <CaretDown size={14} weight="bold" className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" color="var(--bk-text-2)" />
      </div>
    </div>
  );
}
